import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import {ReferredToService} from '../../../../controls/agyle/ecds';
import _ from 'lodash';
import uuidv4 from 'uuid/v4';
import config from '../../../../app/config';
import MessageBox from '../../../../util/messageBox';
import {UtilsMomentJS} from '../../../../store/utilsMoment';
import {useStylesECDS} from './ecdsStyle';
import InformationBox from '../../../../components/other/informationBox';
import useOpenState from '../../../../hooks/useOpenState';
import {AgyleTable, createAgyleTableColumn} from '../../../../components/table/AgyleTable';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import {useDispatch} from 'react-redux';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import {setSnackbar} from '../../../../store/reducers/globals';

const ReferralsOtherServices = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();
    const dispatch = useDispatch();

    // Props
    const {ecdsData, formContext} = props;

    // Multi Entry list
    const [list, setList] = useState([]);

    // Data
    const [dataForm, setDataForm] = useState({
        id: null,
        service: null,
        requestTimestamp: moment(),
        assessmentTimestamp: null,
    });
    const [pickers, setPickers] = useState({
        requestTimestamp: moment(),
        assessmentTimestamp: null,
    });

    // Validation
    const [error, setError] = useState(false);
    const [errorPicker, setErrorPicker] = useState({
        requestTimestamp: null,
        assessmentTimestamp: null,
    });

    // Accordion
    const [expanded, setExpanded] = React.useState('referrals');

    //  For Delete
    const [deleteItem, setDeleteItem] = useState(null);
    const [isConfirmRemoveElementOpen, openConfirmRemoveElement, closeConfirmRemoveElement] = useOpenState(false, true);

    useEffect(() => {
        if (props.onCustomButtons) {
            const buttons = [];
            if (dataForm.id) {
                buttons.push(
                    <AgyleButton
                        key="cancel"
                        colour="primary"
                        onClick={clearData}
                    >
                        {t('Cancel edit')}
                    </AgyleButton>,
                );
            }
            buttons.push([
                <AgyleButton
                    key="add-button"
                    colour="primary"
                    disabled={props.cannotEdit}
                    onClick={addToList}
                >
                    {t(dataForm.id ? 'Save changes' : 'Add Record')}
                </AgyleButton>,
            ]);
            props.onCustomButtons(buttons);
        }
    }, [props.disableSave, props.cannotEdit, dataForm, errorPicker]);

    // Init data
    useEffect(() => {
        mapToList(props.isBB ? props.patient?.specialty?.ReferralsToOtherServices : props?.patient?.studies?.referrals?.ReferralsToOtherServices);
    }, []);

    const mapToList = (values) => {
        const newList = [];
        _.forEach(values, ref => {
            let requestTimestamp;
            let assessmentTimestamp;

            if (ref.ActivityServiceRequestTimestamp_UrgentAndEmergencyCare) {
                requestTimestamp = moment.utc(ref.ActivityServiceRequestTimestamp_UrgentAndEmergencyCare, config.ecdsDateTimeFormat).local();
            } else {
                requestTimestamp = UtilsMomentJS.getLocalDateFromUTCECDSFormat(ref.ActivityServiceRequestDate_EmergencyCare, ref.ActivityServiceRequestTime_EmergencyCare);
            }

            if (ref.ReferredToServiceAssessmentTimestamp) {
                assessmentTimestamp = moment.utc(ref.ReferredToServiceAssessmentTimestamp, config.ecdsDateTimeFormat).local();
            } else {
                assessmentTimestamp = UtilsMomentJS.getLocalDateFromUTCECDSFormat(ref.ReferredToServiceAssessmentDate, ref.ReferredToServiceAssessmentTime);
            }

            newList.push({
                id: ref.id === null ? uuidv4() + '' : ref.id,
                ReferredToService_SnomedCt: ref.ReferredToService_SnomedCt || '',
                ActivityServiceRequestDate_EmergencyCare: ref.ActivityServiceRequestDate_EmergencyCare || '',
                ActivityServiceRequestTime_EmergencyCare: ref.ActivityServiceRequestTime_EmergencyCare || '',
                ActivityServiceRequestTimestamp_UrgentAndEmergencyCare: ref.ActivityServiceRequestTimestamp_UrgentAndEmergencyCare || '',
                ReferredToServiceAssessmentDate: ref.ReferredToServiceAssessmentDate || '',
                ReferredToServiceAssessmentTime: ref.ReferredToServiceAssessmentTime || '',
                ReferredToServiceAssessmentTimestamp: ref.ReferredToServiceAssessmentTimestamp || '',
                rTimestamp: requestTimestamp,
                aTimestamp: assessmentTimestamp,
                timestamp: moment().toISOString(),
            });
        });
        setList(newList);
    };

    const setDataDictionary = () => {
        let formattedList = _.cloneDeep(list);

        for (const element of formattedList) {
            if (ecdsData) {
                delete element.id;
                element.ReferredToService_SnomedCt = element.ReferredToService_SnomedCt.snomedCode;
            }
            delete element.rTimestamp;
            delete element.aTimestamp;
        }
        const data = {ReferralsToOtherServices: formattedList};
        props.setData(data);
    };

    useEffect(() => {
        setDataDictionary();
    }, [list]);

    const validErrorPicker = () => {
        return !errorPicker.assessmentTimestamp && !errorPicker.requestTimestamp;
    };

    const addToList = () => {
        if (validation() && validErrorPicker()) {
            if (props.saveData) props.saveData();
            const data = {
                id: dataForm.id === null ? uuidv4() + '' : dataForm.id,
                ReferredToService_SnomedCt: dataForm.service || '',
                ActivityServiceRequestTimestamp_UrgentAndEmergencyCare: pickers.requestTimestamp ? moment(pickers.requestTimestamp).utc().format(config.ecdsDateTimeFormat) : '',
                ActivityServiceRequestDate_EmergencyCare: pickers.requestTimestamp ? moment(pickers.requestTimestamp).utc().format(config.ecdsDateFormat) : '',
                ActivityServiceRequestTime_EmergencyCare: pickers.requestTimestamp ? moment(pickers.requestTimestamp).utc().format(config.ecdsTimeFormat) : '',
                ReferredToServiceAssessmentTimestamp: pickers.assessmentTimestamp ? moment(pickers.assessmentTimestamp).utc().format(config.ecdsDateTimeFormat) : '',
                ReferredToServiceAssessmentDate: pickers.assessmentTimestamp ? moment(pickers.assessmentTimestamp).utc().format(config.ecdsDateFormat) : '',
                ReferredToServiceAssessmentTime: pickers.assessmentTimestamp ? moment(pickers.assessmentTimestamp).utc().format(config.ecdsTimeFormat) : '',
                rTimestamp: moment(pickers.requestTimestamp).toISOString(),
                aTimestamp: moment(pickers.assessmentTimestamp).toISOString(),
                timestamp: moment().toISOString(),
            };
            const array = _.cloneDeep(list);

            let index = _.findIndex(array, {id: data.id});
            if (index === -1) array.push(data);
            else array[index] = data;

            setList(array);
            clearData();
        }
    };

    const validation = () => {
        if (dataForm.service) {
            if (!moment(dataForm.requestTimestamp).isValid()) {
                dispatch(setSnackbar({
                    type: 'warning',
                    message: 'The selected Activity Service Request timestamp is incorrect.',
                }));
                return false;
            }
            if (dataForm.assessmentTimestamp && !moment(dataForm.assessmentTimestamp).isValid()) {
                dispatch(setSnackbar({
                    type: 'warning',
                    message: 'The selected Referred To Service Assessment Timestamp is incorrect.',
                }));
                return false;
            }

            return true;
        } else {
            dispatch(setSnackbar({type: 'warning', message: 'Please complete Referrals to Service field.'}));
            return false;
        }
    };

    const clearData = () => {
        const clearDataForm = {
            id: null,
            service: null,
            requestTimestamp: moment(),
            assessmentTimestamp: null,
        };
        setDataForm(prevState => ({...prevState, ...clearDataForm}));
        setPickers({
            requestTimestamp: moment(),
            assessmentTimestamp: null,
        });
        setError(false);
    };

    const changeHandler = (event) => {
        const value = event.target.value;

        if (props.checkLenght(6, 18, value)) {
            setDataForm(prevState => ({...prevState, 'service': value}));
        }
    };

    const dateHandler = (name, event) => {
        if (event) {
            const date = moment(event);
            setPickers(prevState => ({...prevState, [name]: date}));
        }
    };

    const setDataFromDictionary = (data) => {
        changeHandler({
            target: {
                name: data[0],
                value: data?.[1] || null,
            },
        });
    };

    const handleAccordionChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    const deleteReferral = () => {
        if (props.saveData) props.saveData();
        setList(prevState => prevState.filter(value => value.id !== deleteItem?.id));
        setDeleteItem(null);
        closeConfirmRemoveElement();
        clearData();
    };

    const editReferral = (referral) => {
        let requestTimestamp;
        let assessmentTimestamp;
        if (referral.ActivityServiceRequestTimestamp_UrgentAndEmergencyCare) {
            requestTimestamp = moment.utc(referral.ActivityServiceRequestTimestamp_UrgentAndEmergencyCare, config.ecdsDateTimeFormat);
        } else {
            requestTimestamp = UtilsMomentJS.getLocalDateFromUTCECDSFormat(referral.ActivityServiceRequestDate_EmergencyCare, referral.ActivityServiceRequestTime_EmergencyCare);
        }
        if (referral.ReferredToServiceAssessmentTimestamp) {
            assessmentTimestamp = moment.utc(referral.ReferredToServiceAssessmentTimestamp, config.ecdsDateTimeFormat);
        } else {
            assessmentTimestamp = UtilsMomentJS.getLocalDateFromUTCECDSFormat(referral.ReferredToServiceAssessmentDate, referral.ReferredToServiceAssessmentTime);
        }

        const editData = {
            id: referral.id,
            service: referral.ReferredToService_SnomedCt,
            requestTimestamp,
            assessmentTimestamp,
        };
        setError(false);

        setDataForm({...editData});
        setPickers({
            requestTimestamp: requestTimestamp || null,
            assessmentTimestamp: assessmentTimestamp || moment(),
        });
    };

    const removeEntry = (entry) => {
        setDeleteItem(entry);
        openConfirmRemoveElement();
    };

    const renderTable = () => {
        const COLUMNS = [
            createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, null, null),
            createAgyleTableColumn('reff', 'Referred to Service', 2, 'ReferredToService_SnomedCt', 'dict', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('rDate', 'Request Date and Time', 3, 'rTimestamp', 'date', '', 'medium', 'left', true, true, null, null, null),
            createAgyleTableColumn('aDate', 'Assessment Date and Time', 4, 'aTimestamp', 'date', '', 'medium', 'left', true, false, null, null, null),
        ];

        if (!ecdsData) COLUMNS.push(
            createAgyleTableColumn('action', 'Action', 7, null, 'action', 'notSort', 'medium', 'left', true, true, null, null, null),
        );

        if (list) {
            return (
                <AgyleTable
                    columns={COLUMNS}
                    data={list}
                    editItem={editReferral}
                    deleteItem={removeEntry}
                />
            );
        }
    };

    return (
        <div className={classes.root}>
            {ecdsData && !_.isEmpty(list) &&
                <div style={{padding: '5px 0px'}}>
                    {renderTable()}
                </div>
            }

            {!ecdsData &&
                <>
                    {!_.isEmpty(list) &&
                        <AgyleAccordion
                            expanded={expanded === 'referrals'}
                            onChange={handleAccordionChange('referrals')}
                            label={t('Table')}
                            formBackground
                        >
                            {renderTable()}
                        </AgyleAccordion>
                    }

                    <Grid container spacing={2} style={{marginTop: 1}}>
                        <Grid item xs={12}>
                            <ReferredToService
                                label={t('Referrals to Service')}
                                onChange={(_e, val) => setDataFromDictionary(['ReferredToService_SnomedCt', val])}
                                error={dataForm.service === null}
                                value={dataForm.service}
                                size="large"
                                noBorder={false}
                                admitDate={props.patient?.admission?.startDate}
                                noBorder={!formContext}
                                withNewBackground={formContext}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <AgyleDatePicker
                                disabled={props.cannotEdit}
                                label={t('Activity Service Request Date and Time')}
                                type="dateTime"
                                noBorder={false}
                                value={pickers.requestTimestamp || null}
                                onChange={(e) => dateHandler('requestTimestamp', e)}
                                disableFuture
                                minDate={props.isBB ? null : moment(calculateArrivalDate(props.patient))}
                                onError={event => setErrorPicker(prevState => ({
                                    ...prevState,
                                    requestTimestamp: event,
                                }))}
                                error={pickers.requestTimestamp === null}
                                size="large"
                                noBorder={!formContext}
                                withNewBackground={formContext}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <AgyleDatePicker
                                disabled={props.cannotEdit}
                                label={t('Service Assessment Date and Time')}
                                type="dateTime"
                                noBorder={false}
                                value={pickers.assessmentTimestamp || null}
                                onChange={(e) => dateHandler('assessmentTimestamp', e)}
                                disableFuture
                                minDate={pickers.requestTimestamp}
                                onError={event => setErrorPicker(prevState => ({
                                    ...prevState,
                                    assessmentTimestamp: event,
                                }))}
                                error={error}
                                size="large"
                                noBorder={!formContext}
                                withNewBackground={formContext}
                            />
                        </Grid>

                        {!formContext &&
                            <Grid item xs={12} className={classes.divRow} style={{justifyContent: 'end', gap: 5}}>
                                <AgyleButton
                                    variant="contained"
                                    disabled={props.cannotEdit}
                                    onClick={addToList}
                                >
                                    {t(dataForm.id ? 'Save Record' : 'Add Record')}
                                </AgyleButton>

                                {dataForm.id && (
                                    <AgyleButton
                                        variant="contained"
                                        onClick={clearData}
                                    >
                                        {t('Cancel')}
                                    </AgyleButton>
                                )}
                            </Grid>
                        }
                    </Grid>
                </>
            }

            {ecdsData && _.isEmpty(list) &&
                <InformationBox type="info">
                    {t('Specialty referrals have not been set up')}
                </InformationBox>
            }

            <MessageBox
                open={isConfirmRemoveElementOpen}
                onClose={closeConfirmRemoveElement}
                title={t('Confirmation')}
                message={t('Do you really want to remove this item?')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={deleteReferral}
                warning
            />
        </div>
    );
};

export default ReferralsOtherServices;
